import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { FiGrid, FiBox, FiPackage, FiUsers, FiShoppingBag, FiMapPin, FiFlag, FiShield, FiChevronDown, FiEdit } from 'react-icons/fi';
import { Globe, MapPin, HelpCircle, BarChart3, Activity, Users, UserCog, Megaphone, MousePointerClick, Fuel, FileText, AlertTriangle, Truck, Map, ClipboardList, Ticket, CreditCard, Bell, MessageSquare } from 'lucide-react';
import { useAuth } from '../context/AuthContext.tsx';
import './Sidebar.css';

interface SidebarProps {
  isCollapsed: boolean;
  isMobileOpen: boolean;
  onMobileClose: () => void;
}

/* ── Collapsible Menu Group ── */
interface MenuGroupProps {
  label: string;
  paths: string[];
  exactPaths?: string[];
  isCollapsed: boolean;
  children: React.ReactNode;
}

const MenuGroup: React.FC<MenuGroupProps> = ({ label, paths, exactPaths, isCollapsed, children }) => {
  const location = useLocation();
  const isActive =
    (exactPaths || []).some(p => location.pathname === p) ||
    paths.some(p => location.pathname === p || location.pathname.startsWith(p + '/'));
  const [open, setOpen] = useState(isActive);
  const contentRef = useRef<HTMLUListElement>(null);
  const [contentHeight, setContentHeight] = useState<number | undefined>(undefined);

  // Auto-expand when a child route becomes active
  useEffect(() => {
    if (isActive) setOpen(true);
  }, [isActive]);

  // Measure content height for smooth animation
  useEffect(() => {
    if (contentRef.current) {
      setContentHeight(contentRef.current.scrollHeight);
    }
  }, [children, open]);

  return (
    <div className={`menu-group ${open ? 'open' : ''}`}>
      <button
        className={`menu-section ${isActive ? 'has-active' : ''}`}
        onClick={() => setOpen(prev => !prev)}
        type="button"
      >
        <span className="menu-section-label">{label}</span>
        {!isCollapsed && <FiChevronDown size={14} className="menu-section-chevron" />}
      </button>
      <ul
        ref={contentRef}
        className="menu-group-items"
        style={{
          maxHeight: open ? (contentHeight ?? 500) : 0,
        }}
      >
        {children}
      </ul>
    </div>
  );
};

const Sidebar: React.FC<SidebarProps> = ({ isCollapsed, isMobileOpen, onMobileClose }) => {
  const { user, hasPermission } = useAuth();

  const userName = user?.username || 'Admin User';
  const userRole = user?.role_name || 'Super Admin';
  const userInitial = userName.charAt(0).toUpperCase();

  return (
    <>
    {isMobileOpen && <div className="sidebar-backdrop" onClick={onMobileClose} />}
    <div className={`sidebar ${isCollapsed ? 'collapsed' : ''} ${isMobileOpen ? 'mobile-open' : ''}`}>
      <div className="sidebar-logo">
        <div className="sidebar-logo-icon">
          <img src="/logo.png" alt="PCF" />
        </div>
        <div className="sidebar-logo-text">
          <h1 className="sidebar-logo-title">PriceCheck</h1>
          <p className="sidebar-logo-subtitle">Admin Portal</p>
        </div>
      </div>

      <nav onClick={onMobileClose}>
        {hasPermission('dashboard') && (
          <MenuGroup label="Main Menu" paths={[]} exactPaths={['/dashboard']} isCollapsed={isCollapsed}>
            <li>
              <NavLink 
                to="/dashboard" 
                end
                className={({ isActive }) => isActive ? 'active' : ''}
              >
                <FiGrid size={20} />
                {!isCollapsed && <span>Dashboard</span>}
              </NavLink>
            </li>
          </MenuGroup>
        )}

        {hasPermission('catalog') && (
          <MenuGroup label="Catalog" paths={['/dashboard/item-kits', '/dashboard/items']} isCollapsed={isCollapsed}>
            <li>
              <NavLink to="/dashboard/item-kits" className={({ isActive }) => isActive ? 'active' : ''}>
                <FiBox size={20} />
                {!isCollapsed && <span>Item Kits</span>}
              </NavLink>
            </li>
            <li>
              <NavLink to="/dashboard/items" className={({ isActive }) => isActive ? 'active' : ''}>
                <FiPackage size={20} />
                {!isCollapsed && <span>Items</span>}
              </NavLink>
            </li>
          </MenuGroup>
        )}

        {hasPermission('management') && (
          <MenuGroup
            label="Management"
            paths={[
              '/dashboard/users', '/dashboard/user-activity', '/dashboard/reported-posts',
              '/dashboard/create-post', '/dashboard/support', '/dashboard/marketing-messages',
              '/dashboard/delivery-addresses', '/dashboard/retailers', '/dashboard/coupons',
              '/dashboard/bulk-notifications',
              '/dashboard/ads-config',
              '/dashboard/cache-config',
            ]}
            isCollapsed={isCollapsed}
          >
            <li>
              <NavLink to="/dashboard/users" className={({ isActive }) => isActive ? 'active' : ''}>
                <FiUsers size={20} />
                {!isCollapsed && <span>Users</span>}
              </NavLink>
            </li>
            <li>
              <NavLink to="/dashboard/user-activity" className={({ isActive }) => isActive ? 'active' : ''}>
                <Users size={20} />
                {!isCollapsed && <span>User Activity</span>}
              </NavLink>
            </li>
            <li>
              <NavLink to="/dashboard/reported-posts" className={({ isActive }) => isActive ? 'active' : ''}>
                <FiFlag size={20} />
                {!isCollapsed && <span>Reported Posts</span>}
              </NavLink>
            </li>
            <li>
              <NavLink to="/dashboard/create-post" className={({ isActive }) => isActive ? 'active' : ''}>
                <FiEdit size={20} />
                {!isCollapsed && <span>Create Post</span>}
              </NavLink>
            </li>
            <li>
              <NavLink to="/dashboard/support" className={({ isActive }) => isActive ? 'active' : ''}>
                <HelpCircle size={20} />
                {!isCollapsed && <span>Support</span>}
              </NavLink>
            </li>
            <li>
              <NavLink to="/dashboard/marketing-messages" className={({ isActive }) => isActive ? 'active' : ''}>
                <Megaphone size={20} />
                {!isCollapsed && <span>Marketing Messages</span>}
              </NavLink>
            </li>
            <li>
              <NavLink to="/dashboard/delivery-addresses" className={({ isActive }) => isActive ? 'active' : ''}>
                <FiMapPin size={20} />
                {!isCollapsed && <span>Delivery Addresses</span>}
              </NavLink>
            </li>
            <li>
              <NavLink to="/dashboard/retailers" className={({ isActive }) => isActive ? 'active' : ''}>
                <FiShoppingBag size={20} />
                {!isCollapsed && <span>Retailers</span>}
              </NavLink>
            </li>
            <li>
              <NavLink to="/dashboard/coupons" className={({ isActive }) => isActive ? 'active' : ''}>
                <Ticket size={20} />
                {!isCollapsed && <span>Coupons</span>}
              </NavLink>
            </li>
            <li>
              <NavLink to="/dashboard/bulk-notifications" className={({ isActive }) => isActive ? 'active' : ''}>
                <Bell size={20} />
                {!isCollapsed && <span>Bulk Notifications</span>}
              </NavLink>
            </li>
            <li>
              <NavLink to="/dashboard/ads-config" className={({ isActive }) => isActive ? 'active' : ''}>
                <FileText size={20} />
                {!isCollapsed && <span>Ads</span>}
              </NavLink>
            </li>
            <li>
              <NavLink to="/dashboard/cache-config" className={({ isActive }) => isActive ? 'active' : ''}>
                <Activity size={20} />
                {!isCollapsed && <span>Cache</span>}
              </NavLink>
            </li>
          </MenuGroup>
        )}

        {hasPermission('analytics') && (
          <MenuGroup
            label="Analytics"
            paths={['/dashboard/search-analytics', '/dashboard/performance-analytics', '/dashboard/product-clicks', '/dashboard/crash-reports']}
            isCollapsed={isCollapsed}
          >
            <li>
              <NavLink to="/dashboard/search-analytics" className={({ isActive }) => isActive ? 'active' : ''}>
                <BarChart3 size={20} />
                {!isCollapsed && <span>Search Analytics</span>}
              </NavLink>
            </li>
            <li>
              <NavLink to="/dashboard/performance-analytics" className={({ isActive }) => isActive ? 'active' : ''}>
                <Activity size={20} />
                {!isCollapsed && <span>Performance Analytics</span>}
              </NavLink>
            </li>
            <li>
              <NavLink to="/dashboard/product-clicks" className={({ isActive }) => isActive ? 'active' : ''}>
                <MousePointerClick size={20} />
                {!isCollapsed && <span>Product Clicks</span>}
              </NavLink>
            </li>
            <li>
              <NavLink to="/dashboard/crash-reports" className={({ isActive }) => isActive ? 'active' : ''}>
                <AlertTriangle size={20} />
                {!isCollapsed && <span>Crash Reports</span>}
              </NavLink>
            </li>
          </MenuGroup>
        )}

        {hasPermission('fuel') && (
          <MenuGroup
            label="Fuel"
            paths={['/dashboard/fuel-stations', '/dashboard/fuel-reports', '/dashboard/fuel-analytics']}
            isCollapsed={isCollapsed}
          >
            <li>
              <NavLink to="/dashboard/fuel-stations" className={({ isActive }) => isActive ? 'active' : ''}>
                <Fuel size={20} />
                {!isCollapsed && <span>Fuel Stations</span>}
              </NavLink>
            </li>
            <li>
              <NavLink to="/dashboard/fuel-reports" className={({ isActive }) => isActive ? 'active' : ''}>
                <FileText size={20} />
                {!isCollapsed && <span>Price Reports</span>}
              </NavLink>
            </li>
            <li>
              <NavLink to="/dashboard/fuel-analytics" className={({ isActive }) => isActive ? 'active' : ''}>
                <BarChart3 size={20} />
                {!isCollapsed && <span>Fuel Analytics</span>}
              </NavLink>
            </li>
          </MenuGroup>
        )}

        {hasPermission('geography') && (
          <MenuGroup label="Geography" paths={['/dashboard/states', '/dashboard/countries', '/dashboard/deliverable-postcodes']} isCollapsed={isCollapsed}>
            <li>
              <NavLink to="/dashboard/states" className={({ isActive }) => isActive ? 'active' : ''}>
                <MapPin size={20} />
                {!isCollapsed && <span>States</span>}
              </NavLink>
            </li>
            <li>
              <NavLink to="/dashboard/countries" className={({ isActive }) => isActive ? 'active' : ''}>
                <Globe size={20} />
                {!isCollapsed && <span>Countries</span>}
              </NavLink>
            </li>
            <li>
              <NavLink to="/dashboard/deliverable-postcodes" className={({ isActive }) => isActive ? 'active' : ''}>
                <FiMapPin size={20} />
                {!isCollapsed && <span>Deliverable Postcodes</span>}
              </NavLink>
            </li>
          </MenuGroup>
        )}

        {hasPermission('orders') && (
          <MenuGroup label="Orders & Delivery" paths={['/dashboard/zones', '/dashboard/drivers', '/dashboard/orders', '/dashboard/payment-gateways']} isCollapsed={isCollapsed}>
            <li>
              <NavLink to="/dashboard/zones" className={({ isActive }) => isActive ? 'active' : ''}>
                <Map size={20} />
                {!isCollapsed && <span>Zones</span>}
              </NavLink>
            </li>
            <li>
              <NavLink to="/dashboard/drivers" className={({ isActive }) => isActive ? 'active' : ''}>
                <Truck size={20} />
                {!isCollapsed && <span>Drivers</span>}
              </NavLink>
            </li>
            <li>
              <NavLink to="/dashboard/orders" className={({ isActive }) => isActive ? 'active' : ''}>
                <ClipboardList size={20} />
                {!isCollapsed && <span>Orders</span>}
              </NavLink>
            </li>
            <li>
              <NavLink to="/dashboard/payment-gateways" className={({ isActive }) => isActive ? 'active' : ''}>
                <CreditCard size={20} />
                {!isCollapsed && <span>Payment Gateways</span>}
              </NavLink>
            </li>
          </MenuGroup>
        )}

        {hasPermission('feedback_campaigns') && (
          <MenuGroup label="Feedback" paths={['/dashboard/feedback-campaigns']} isCollapsed={isCollapsed}>
            <li>
              <NavLink to="/dashboard/feedback-campaigns" className={({ isActive }) => isActive ? 'active' : ''}>
                <MessageSquare size={20} />
                {!isCollapsed && <span>Campaigns</span>}
              </NavLink>
            </li>
          </MenuGroup>
        )}

        {hasPermission('superuser_mgmt') && (
          <MenuGroup label="System" paths={['/dashboard/roles', '/dashboard/superusers']} isCollapsed={isCollapsed}>
            <li>
              <NavLink to="/dashboard/roles" className={({ isActive }) => isActive ? 'active' : ''}>
                <FiShield size={20} />
                {!isCollapsed && <span>Roles</span>}
              </NavLink>
            </li>
            <li>
              <NavLink to="/dashboard/superusers" className={({ isActive }) => isActive ? 'active' : ''}>
                <UserCog size={20} />
                {!isCollapsed && <span>Super Users</span>}
              </NavLink>
            </li>
          </MenuGroup>
        )}
      </nav>

      <div className="sidebar-footer">
        <div className="sidebar-footer-avatar">{userInitial}</div>
        <div className="sidebar-footer-info">
          <p className="sidebar-footer-name">{userName}</p>
          <p className="sidebar-footer-role">{userRole}</p>
        </div>
      </div>
    </div>
    </>
  );
};

export default Sidebar;
